<template>
  <a-card
    :id="`objective-${objective.id}`"
    :bordered="false"
  >
    <a-table
      :columns="columns"
      :data-source="data"
      :pagination="false"
      :expanded-row-keys="expandedRowKeys"
      :expand-icon="() => null"
      :expand-icon-as-cell="false"
      :components="components"
    >
      <template #icon="{ index, record }">
        <div class="key-result-icon-cell">
          <ToBeAlignedDot
            v-if="!canEdit && doesNeedAlign(record)"
            class="to-be-aligned-dot"
          />
          <MenuOutlined
            v-if="canEdit"
            class="kr-drag-icon"
          />
          <key-result-icon
            :index="index + 1"
            class="key-result-icon"
          />
        </div>
      </template>
      <template #objectiveContent>
        <ObjectiveContent
          :index="objectiveIndex"
          :objective="objective"
          :can-edit="canEdit"
          v-bind="$attrs"
          :is-my-management-scope="isMyManagementScope"
          @click-objective="onClickObjectiveContent"
        />
      </template>
      <template #content="{ index, text, record }">
        <div
          :class="{ clickable: canEdit }"
          @click="onClickKeyResultContent(index, record)"
          v-html="text"
        />
        <AlignUserForPerformance
          :entity="{
            id: record.id,
            type: 'keyResult',
            statementId: record.statementId.id
          }"
          :aligned-users="record.alignedFromUsers"
        />
      </template>
      <template #objectiveWeight>
        <edit-weight
          :objective="objective"
          :can-edit="canEdit"
          v-bind="$attrs"
          @weight-updated="$emit('weight-updated')"
        />
      </template>
      <template #weight />
      <template #objectiveAlignment>
        <AlignDialog
          v-if="canReview"
          :entity="{
            id: objective.id,
            statementId: objective.statementId.id,
            type: 'objective',
            content: objective.content,
            mentionedUserIds: objective.mentionUserIds,
            alignedStatementIds: objective.alignedToIds,
          }"
          v-bind="$attrs"
          @invited="updatedObjective => $emit('objective-invited', updatedObjective)"
        />
        <AlignMyselfDialog
          v-else
          :entity="{
            id: objective.id,
            type: 'objective',
            statementId: objective.statementId.id,
            alignedStatementIds: objective.alignedFromIds
          }"
          :aligned-from-ids-change-type="alignedFromIdsChangeType"
          :my-okr-items="myOkrItems"
          :user-id="userId"
          @aligned="updatedObjective => $emit('objective-aligned-myself', updatedObjective)"
        />
      </template>
      <template #keyResultAlignment="{ record, index }">
        <AlignInvitationDialog
          v-if="canReview"
          :entity="{
            id: record.id,
            type: 'keyResult',
            mentionedUserIds: record.mentionUserIds,
            content: record.content
          }"
          @invited="updatedKeyResult => $emit('key-result-invited', updatedKeyResult, index)"
        />
        <AlignMyselfDialog
          v-else
          :entity="{
            id: record.id,
            type: 'keyResult',
            statementId: record.statementId.id,
            alignedStatementIds: record.alignedFromIds
          }"
          :aligned-from-ids-change-type="alignedFromIdsChangeType"
          :my-okr-items="myOkrItems"
          :user-id="userId"
          @aligned="updatedKeyResult => $emit('key-result-aligned-myself', updatedKeyResult, index)"
        />
      </template>
      <template #objectiveProgress>
        {{ $t('shared.progress') }}: {{ objective.progress }}%
      </template>
      <template #progress="{ record }">
        <ProgressDialog
          :key-result="record"
          :can-edit="canReview"
          @click.stop
          @progress-updated="$emit('progress-updated')"
        />
      </template>
      <template #footer>
        <CreateButton
          v-if="canEdit"
          :text="$t('okr.list.createKeyResult')"
          icon-name="icontianjiaKR1"
          :style="{ margin: '16px 0 16px 9px', backgroundColor: '#F8F9FF' }"
          @click="$emit('create-key-result')"
        />
        <ObjectiveReview
          :objective="objective"
          :can-edit="canReview"
          v-bind="$attrs"
        />
        <performance-review
          v-if="isMyManagementScope && lockStatus"
          :performance-objective-self-review="objective.performanceObjectiveSelfReview"
        />
      </template>
      <template
        v-if="canEdit"
        #objectiveAction
      >
        <a-dropdown>
          <EllipsisOutlined @click.stop />
          <template #overlay>
            <a-menu>
              <a-menu-item
                @click="onDeleteObjective"
              >
                {{ $t('okr.list.delete') }}
              </a-menu-item>
            </a-menu>
          </template>
        </a-dropdown>
      </template>
      <template
        v-if="canEdit"
        #keyResultAction="{ record, index }"
      >
        <a-dropdown>
          <EllipsisOutlined @click.stop />
          <template #overlay>
            <a-menu>
              <a-menu-item
                @click="onDeleteKeyResult(record.id, index)"
              >
                {{ $t('okr.list.delete') }}
              </a-menu-item>
            </a-menu>
          </template>
        </a-dropdown>
      </template>
      <template #objectiveComment>
        <a-space
          class="clickable"
          @click.stop="isObjectiveCommentVisible = !isObjectiveCommentVisible"
        >
          <IconFont
            type="iconpinglun"
            class="icon-block"
          />
          <span v-if="isObjectiveCommentVisible">
            {{ $t('okr.list.collapseComment') }}
          </span>
          <span v-else>
            {{ objective.commentCount }}
          </span>
        </a-space>
      </template>
      <template #comment="{ record }">
        <a-space
          class="clickable"
          @click="handleExpand(record.key)"
        >
          <IconFont
            type="iconpinglun"
            class="icon-block"
          />
          <span v-if="expandedRowKeys.includes(record.key)">
            {{ $t('okr.list.collapseComment') }}
          </span>
          <span v-else>
            {{ record.commentCount }}
          </span>
        </a-space>
      </template>
      <template #expandedRowRender="{ record }">
        <Comment
          :entity="{
            id: record.id,
            type: 'keyResult'
          }"
        />
      </template>
    </a-table>
  </a-card>
</template>

<script>
import cloneDeep from 'lodash/cloneDeep'
import { ref, computed, toRaw, createVNode, onMounted, h, provide } from 'vue'
import { useStore } from 'vuex'
import { useI18n } from 'vue-i18n'
import { EllipsisOutlined, ExclamationCircleOutlined, MenuOutlined } from '@ant-design/icons-vue'
import { Modal, message } from 'ant-design-vue'
import Sortable from 'sortablejs'

import { replaceHtml2Str } from '@/utils/line-break'
import { deleteObjective, deleteKeyResult } from '@/api/okr'
import useOkr from '@/composables/use-okr'
import KeyResultIcon from '@/components/KeyResultIcon'
import CreateButton from '@/components/CreateButton'
import ObjectiveContent from './ObjectiveContent'
// import AlignedUsers from './AlignedUsers'
import AlignUserForPerformance from './AlignedUsersForPerformance/index.vue'
import AlignDialog from '@/components/AlignDialog'
import AlignInvitationDialog from '@/components/AlignDialog/AlignInvitationDialog'
import AlignMyselfDialog from '@/components/AlignDialog/AlignMyselfDialog'
import ProgressDialog from './ProgressDialog'
import ToBeAlignedDot from '@/components/ToBeAlignedDot'
import Comment from './Comment'
import HeaderWrapper from './HeaderWrapper'
import EditWeight from './EditWeight.vue'
import ObjectiveReview from './ObjectiveReview/index'
import PerformanceReview from './PerformanceReview.vue'

export default {
  name: 'ObjectiveCard',
  components: {
    EllipsisOutlined,
    MenuOutlined,
    KeyResultIcon,
    CreateButton,
    ObjectiveContent,
    // AlignedUsers,
    AlignUserForPerformance,
    AlignDialog,
    AlignInvitationDialog,
    AlignMyselfDialog,
    ProgressDialog,
    ToBeAlignedDot,
    Comment,
    ObjectiveReview,
    EditWeight,
    PerformanceReview
  },
  inheritAttrs: false,
  props: {
    objectiveIndex: {
      type: Number,
      required: true
    },
    canEdit: {
      type: Boolean,
      required: true
    },
    canReview: {
      type: Boolean,
      required: true
    },
    columns: {
      type: Array,
      required: true
    },
    data: {
      type: Array,
      required: true
    },
    objective: {
      type: Object,
      default: () => null
    },
    isMyManagementScope: {
      type: Boolean,
      default: true
    },
    alignedFromIdsChangeType: {
      type: String,
      required: true
    },
    userId: {
      type: Number,
      default: undefined
    }
  },
  emits: [
    'create-key-result',
    'edit-objective',
    'edit-key-result',
    'objective-deleted',
    'key-result-deleted',
    'progress-updated',
    'weight-updated',
    'objective-invited',
    'key-result-invited',
    'objective-aligned-myself',
    'key-result-aligned-myself',
    'reorder'
  ],
  setup (props, { emit }) {
    const { state, getters } = useStore()
    const { t } = useI18n()
    const user = computed(() => state.user)
    const objectiveWeight = computed(() => props.data.reduce((sum, item) => sum + item.weight, 0))
    const myOkrItems = ref([])
    const { getPublishedOkrByCycle } = useOkr()
    const expandedRowKeys = ref([])
    const isObjectiveCommentVisible = ref(false)

    const lockStatus = computed(() => getters.lockStatus)

    provide('isVisible', isObjectiveCommentVisible)

    onMounted(() => {
      const elem = document.querySelector(`#objective-${props.objective.id} .ant-table-tbody`)
      Sortable.create(elem, {
        handle: '.kr-drag-icon',
        animation: 180,
        delay: 0,
        onStart () {
          expandedRowKeys.value = []
        },
        onEnd ({ newIndex, oldIndex }) {
          emit('reorder', newIndex, oldIndex)
        }
      })
    })

    if (!props.canEdit) {
      getPublishedOkrByCycle(user.value.id)
        .then(res => {
          myOkrItems.value = res.objectives.reduce((arr, objective) => {
            arr.push({
              id: objective.statementId.id,
              content: objective.content,
              type: 'objective',
              realId: objective.id
            })
            return arr
          }, [])
        })
    }

    function onClickObjectiveContent () {
      if (props.canEdit) {
        const objective = toRaw(props.objective)
        const data = {
          id: objective.id,
          content: replaceHtml2Str(objective.content),
          isFinancial: objective.isFinancial,
          mentionUserIds: objective.mentionUserIds,
          performanceIsHide: objective.performanceIsHide,
          performanceObjective: JSON.parse(objective.performanceObjective || '{}'),
          weight: objective.weight
        }
        emit('edit-objective', data.id, data)
      }
    }

    function onClickKeyResultContent (index, record) {
      if (props.canEdit) {
        const data = cloneDeep(toRaw(record))
        data.content = replaceHtml2Str(data.content)
        emit('edit-key-result', data, index)
      }
    }

    function onDeleteObjective () {
      Modal.confirm({
        icon: createVNode(ExclamationCircleOutlined),
        content: t('okr.deleteObjectiveDialog.description'),
        okType: 'primary',
        okButtonProps: {
          danger: true
        },
        onOk: () => {
          return deleteObjective(props.objective.id)
            .then(() => {
              message.success(t('okr.deleteObjectiveDialog.successMessage'))
              emit('objective-deleted')
            })
        }
      })
    }

    function onDeleteKeyResult (keyResultId, index) {
      Modal.confirm({
        icon: createVNode(ExclamationCircleOutlined),
        content: t('okr.deleteKeyResultDialog.description'),
        okType: 'primary',
        okButtonProps: {
          danger: true
        },
        onOk: () => {
          return deleteKeyResult(keyResultId)
            .then(() => {
              message.success(t('okr.deleteKeyResultDialog.successMessage'))
              emit('key-result-deleted', index)
            })
        }
      })
    }

    function doesNeedAlign (keyResult) {
      return !props.canEdit &&
        keyResult.mentionUserIds.includes(user.value.uid) &&
        !keyResult.alignedFromUsers.some(alignedUser => alignedUser.id === user.value.uid)
    }

    function handleExpand (rowKey) {
      if (!expandedRowKeys.value.includes(rowKey)) {
        expandedRowKeys.value.push(rowKey)
      } else {
        expandedRowKeys.value = expandedRowKeys.value.filter(key => key !== rowKey)
      }
    }

    return {
      user,
      objectiveWeight,
      doesNeedAlign,
      myOkrItems,
      expandedRowKeys,
      onDeleteObjective,
      onDeleteKeyResult,
      onClickObjectiveContent,
      onClickKeyResultContent,
      handleExpand,
      lockStatus,
      isObjectiveCommentVisible,
      components: {
        header: {
          wrapper: h(HeaderWrapper, {
            entity: {
              type: 'objective',
              id: props.objective.id
            }
          })
        }
      }
    }
  }
}
</script>

<style lang="less" scoped>
:deep(.ant-card-body) {
  padding: 0;
}

:deep(.ant-table-thead) {
  th {
    vertical-align: top;
    background-color: transparent !important;
  }

  & > tr > th {
    padding: 12px 0;
  }

  th:nth-child(n + 3) {
    text-align: center;
  }

  & > tr > th:first-child {
    padding: 12px 0 12px 16px;
  }

  & > tr > th:nth-child(2) {
    // padding: 12px 16px;
    padding: 12px 16px 12px 0;
  }
}

:deep(.ant-table-tbody) {
  td {
    vertical-align: top;
  }

  & > tr > td {
    border-bottom: none;
    padding: 12px 0;
  }

  td:nth-child(n + 3) {
    text-align: center;
  }

  & > tr > td:first-child {
    padding: 12px 0 12px 16px;
  }

  & > tr > td:nth-child(2) {
    // padding: 12px 16px;
    padding: 12px 16px 12px 0;
  }
}

:deep(.ant-table-placeholder) {
  display: none;
}

:deep(.ant-table-footer) {
  padding: 0;
}

:deep(.ant-collapse-borderless) {
  background-color: transparent !important;
}

:deep(.ant-collapse-header) {
  border-top: 1px solid #f0f0f0;
}

.error {
  color: red;
}

.key-result-icon-cell {
  position: relative;
  padding-left: 10px;
}

.to-be-aligned-dot {
  position: absolute;
  left: 0;
  top: 4px;
}

.key-result-icon {
  margin-top: 2px;
  margin-right: 8px;
}
</style>
